import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { shallowEqual, useDispatch, useSelector } from "react-redux";

import { IRootState } from "config/redux";
import { reset } from "config/redux/slice";
import { ServiceConstants } from "constant";
import { BankSelectionService, ContractService } from "services";
import { BankSpecificStatus } from "types";

import {
  CONTRACT_BANK_STATUSES,
  SELECTABLE_BANKS,
  SelectableBank,
} from "../../bank-selection/constants";
import { ContractDetails } from "../../bank-selection/hooks/types";
import { pickActiveContract } from "../../bank-selection/hooks/utils";

const {
  REQUEST_BANK_SPECIFIC_GET_ID,
  REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID,
  REQUEST_CONTRACT_GET_ID,
  REQUEST_CONTRACT_UPDATE_MULTIPLE_ID,
  REQUEST_ERROR,
  REQUEST_PENDING,
  REQUEST_SUCCESS,
} = ServiceConstants;

// Own request ids, so this quick view never interferes with the state of "Selekcija banaka".
const bankSpecificRequestId = (bank: SelectableBank) =>
  `${REQUEST_BANK_SPECIFIC_GET_ID}PAYMENT_TAB_${bank}`;
const contractRequestId = (bank: SelectableBank) => `${REQUEST_CONTRACT_GET_ID}_PAYMENT_TAB_${bank}`;

const SAVE_DELAY = 1000;
const REFRESH_DELAY = 1000;

export interface UseApprovedContractReturn {
  bank: SelectableBank | undefined;
  contract: ContractDetails | undefined;
  loading: boolean;
  updateContract: (updates: Partial<ContractDetails>) => void;
}

/**
 * Resolves the contract ("O kreditu") of the most advanced approved bank for a bank credit request
 * and keeps it in sync in both directions with the section in "Selekcija banaka".
 */
export const useApprovedContract = (bankCreditRequestId?: number): UseApprovedContractReturn => {
  const [bankStatuses, setBankStatuses] = useState<
    Partial<Record<SelectableBank, BankSpecificStatus | null>>
  >({});
  const [contract, setContract] = useState<ContractDetails | undefined>();
  const dispatch = useDispatch();

  const saveTimeoutRefs = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const refreshTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  const bankSpecificResponses = useSelector(
    (state: IRootState) =>
      SELECTABLE_BANKS.map((bank) => state.service.object[bankSpecificRequestId(bank)]),
    shallowEqual,
  );
  const bankStatusChangeResponses = useSelector(
    (state: IRootState) =>
      SELECTABLE_BANKS.map((bank) => state.service.object[REQUEST_BANK_SPECIFIC_STATUS_CHANGE_ID + bank]),
    shallowEqual,
  );
  const contractUpdateResponse = useSelector(
    (state: IRootState) => state.service.object[REQUEST_CONTRACT_UPDATE_MULTIPLE_ID],
  );

  const fetchBankStatuses = useCallback(() => {
    if (!bankCreditRequestId) return;
    SELECTABLE_BANKS.forEach((bank) =>
      BankSelectionService.bankSpecificGet(bank, bankCreditRequestId, bankSpecificRequestId(bank)),
    );
  }, [bankCreditRequestId]);

  useEffect(() => {
    setBankStatuses({});
    setContract(undefined);
    fetchBankStatuses();
  }, [fetchBankStatuses]);

  // Status changed in "Selekcija banaka" -> the approved bank may be different now
  useEffect(() => {
    if (bankStatusChangeResponses.some((response) => response?.status === REQUEST_SUCCESS)) {
      fetchBankStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankStatusChangeResponses]);

  useEffect(() => {
    SELECTABLE_BANKS.forEach((bank, index) => {
      const response = bankSpecificResponses[index];
      if (response?.status !== REQUEST_SUCCESS && response?.status !== REQUEST_ERROR) return;

      const status: BankSpecificStatus | null =
        response.status === REQUEST_SUCCESS ? response.data?.bankStatus ?? null : null;
      setBankStatuses((prev) => (prev[bank] === status ? prev : { ...prev, [bank]: status }));
    });
  }, [bankSpecificResponses]);

  // Most advanced contract status wins, ties go to the first bank in tab order
  const bank = useMemo(() => {
    let selected: { bank: SelectableBank; rank: number } | undefined;
    SELECTABLE_BANKS.forEach((item) => {
      const status = bankStatuses[item];
      const rank = status ? CONTRACT_BANK_STATUSES.indexOf(status) : -1;
      if (rank > (selected?.rank ?? -1)) {
        selected = { bank: item, rank };
      }
    });
    return selected?.bank;
  }, [bankStatuses]);

  const contractResponse = useSelector((state: IRootState) =>
    bank ? state.service.object[contractRequestId(bank)] : undefined,
  );

  const fetchContract = useCallback(() => {
    if (bank && bankCreditRequestId) {
      ContractService.getContract(bankCreditRequestId, bank, contractRequestId(bank));
    }
  }, [bank, bankCreditRequestId]);

  useEffect(() => {
    if (!bank) {
      setContract(undefined);
      return;
    }
    fetchContract();
  }, [bank, fetchContract]);

  useEffect(() => {
    if (contractResponse?.status !== REQUEST_SUCCESS) return;
    // Don't overwrite values the user is still typing
    if (saveTimeoutRefs.current.size > 0) return;

    const { contract: activeContract } = pickActiveContract(
      contractResponse.data as ContractDetails | ContractDetails[] | undefined,
    );
    setContract(activeContract?.id ? activeContract : undefined);
  }, [contractResponse]);

  // A contract field was saved (here or in "O kreditu") -> refresh this copy
  useEffect(() => {
    if (contractUpdateResponse?.status !== REQUEST_SUCCESS) return;

    clearTimeout(refreshTimeoutRef.current);
    refreshTimeoutRef.current = setTimeout(() => {
      if (saveTimeoutRefs.current.size === 0) {
        fetchContract();
      }
    }, REFRESH_DELAY);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contractUpdateResponse]);

  // Drop this view's responses, so another client never starts from them
  useEffect(
    () => () => {
      clearTimeout(refreshTimeoutRef.current);
      SELECTABLE_BANKS.forEach((item) => {
        dispatch(reset(bankSpecificRequestId(item)));
        dispatch(reset(contractRequestId(item)));
      });
    },
    [dispatch],
  );

  const save = useCallback(
    async (contractId: number, name: string, value: unknown) => {
      await ContractService.contractUpdateMultipleProperties(contractId, { [name]: value });
      saveTimeoutRefs.current.delete(name);

      // Refresh "O kreditu" once all pending changes are saved
      if (saveTimeoutRefs.current.size === 0 && bank && bankCreditRequestId) {
        ContractService.getContract(bankCreditRequestId, bank);
      }
    },
    [bank, bankCreditRequestId],
  );

  const updateContract = useCallback(
    (updates: Partial<ContractDetails>) => {
      const contractId = contract?.id;
      if (!contractId) return;

      setContract((prev) => (prev ? { ...prev, ...updates } : prev));

      Object.entries(updates).forEach(([name, value]) => {
        clearTimeout(saveTimeoutRefs.current.get(name));
        saveTimeoutRefs.current.set(
          name,
          setTimeout(() => save(contractId, name, value), SAVE_DELAY),
        );
      });
    },
    [contract?.id, save],
  );

  const loading =
    !!bankCreditRequestId &&
    (bankSpecificResponses.some((response) => !response || response.status === REQUEST_PENDING) ||
      contractResponse?.status === REQUEST_PENDING);

  return { bank, contract, loading, updateContract };
};
